import React, { useState } from "react";
import { X, Plus, Trash2, ArrowRight, Calendar, Music2, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

// Local-only stable id for React keys when the user mutates the plan list.
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);

const withUid = (items) => items.map((it) => ({ ...it, _uid: uid() }));
const stripUid = (items) =>
  items.map(({ _uid, ...rest }) => rest); // eslint-disable-line no-unused-vars

const PRESETS = [
  {
    label: "Strength Day",
    title: "Heavy Lift",
    plan: [
      { kind: "reps", label: "Squats 5x5", target_count: 25 },
      { kind: "reps", label: "Bench Press 4x6", target_count: 24 },
      { kind: "reps", label: "Rows 3x8", target_count: 24 },
      { kind: "time", label: "Cool-down stretch", target_seconds: 5 * 60 },
    ],
  },
  {
    label: "Burpee Test",
    title: "Burpee Set Test",
    plan: [
      { kind: "reps", label: "Set 1 · 35 burpees", target_count: 35 },
      { kind: "reps", label: "Set 2 · 25 push-ups", target_count: 25 },
      { kind: "time", label: "Set 3 · 60s plank", target_seconds: 60 },
      { kind: "reps", label: "Set 4 · 50 air squats", target_count: 50 },
    ],
  },
  {
    label: "Run Together",
    title: "Run & Catch Up",
    plan: [
      { kind: "time", label: "Warm-up jog · 5 min", target_seconds: 5 * 60 },
      { kind: "time", label: "Tempo run · 15 min", target_seconds: 15 * 60 },
      { kind: "time", label: "Sprint intervals · 5 min", target_seconds: 5 * 60 },
      { kind: "time", label: "Cool-down walk · 5 min", target_seconds: 5 * 60 },
    ],
  },
];

const ProposeSetTestModal = ({ match, onClose, onProposed }) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(6, 0, 0, 0);

  const [date, setDate] = useState(tomorrow.toISOString().slice(0, 10));
  const [time, setTime] = useState("06:00");
  const [title, setTitle] = useState("Leg Day");
  const [location, setLocation] = useState(match?.other_user?.gym || "");
  const [playlist, setPlaylist] = useState("");
  const [plan, setPlan] = useState(() =>
    withUid([
      { kind: "reps", label: "Set 1 · 35 burpees", target_count: 35 },
      { kind: "reps", label: "Set 2 · 25 push-ups", target_count: 25 },
      { kind: "time", label: "Set 3 · 60s plank", target_seconds: 60 },
      { kind: "reps", label: "Set 4 · 50 air squats", target_count: 50 },
    ])
  );
  const [saving, setSaving] = useState(false);

  const applyPreset = (p) => {
    setTitle(p.title);
    setPlan(withUid(p.plan));
  };

  const updateItem = (i, patch) => setPlan((p) => p.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeItem = (i) => setPlan((p) => p.filter((_, idx) => idx !== i));
  const addItem = () =>
    setPlan((p) => [...p, { _uid: uid(), kind: "reps", label: `Set ${p.length + 1}`, target_count: 10 }]);

  const submit = async (e) => {
    e.preventDefault();
    if (plan.length === 0) {
      toast.error("Add at least one set or time block.");
      return;
    }
    setSaving(true);
    try {
      const localDt = new Date(`${date}T${time}:00`);
      await api.post("/set-tests", {
        match_id: match.id,
        title,
        scheduled_at: localDt.toISOString(),
        location,
        playlist_url: playlist || undefined,
        plan: stripUid(plan),
      });
      onProposed?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not propose Set Test.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl flex items-end sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      data-testid="propose-set-test-modal"
    >
      <div className="w-full max-w-lg bg-[#111] border border-white/10 rounded-3xl p-5 sm:p-7 relative my-4">
        <button
          onClick={onClose}
          data-testid="propose-close-btn"
          className="absolute top-4 right-4 text-neutral-400 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Calendar className="text-[#C6FF00]" size={18} />
          <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold">
            Set Test
          </p>
        </div>
        <h2 className="font-heading text-3xl mb-1">Propose a Set Test</h2>
        <p className="text-sm text-neutral-400 mb-5">
          With {match.other_user.name}. Pass the test together — show up, log sets, get the result.
        </p>

        {/* Presets */}
        <div className="flex flex-wrap gap-2 mb-5">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p)}
              data-testid={`propose-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border border-white/15 text-neutral-300 hover:border-[#C6FF00] hover:text-[#C6FF00] transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-1.5">
              Title
            </label>
            <input
              data-testid="propose-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="onboard-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-1.5">
                Date
              </label>
              <input
                data-testid="propose-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="onboard-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-1.5">
                Time
              </label>
              <input
                data-testid="propose-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                className="onboard-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold mb-1.5 flex items-center gap-1.5">
              <MapPin size={11} /> Location
            </label>
            <input
              data-testid="propose-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Gym, park, studio…"
              className="onboard-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold mb-1.5 flex items-center gap-1.5">
              <Music2 size={11} /> Playlist URL (optional)
            </label>
            <input
              data-testid="propose-playlist"
              value={playlist}
              onChange={(e) => setPlaylist(e.target.value)}
              placeholder="Spotify or Apple Music link"
              className="onboard-input"
            />
          </div>

          {/* Plan builder */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">
                Workout Plan
              </span>
              <button
                type="button"
                onClick={addItem}
                data-testid="propose-add-item"
                className="text-[10px] uppercase tracking-wider text-[#C6FF00] font-bold flex items-center gap-1"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {plan.map((item, i) => (
                <div
                  key={item._uid}
                  data-testid={`propose-plan-item-${i}`}
                  className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3 flex items-center gap-2"
                >
                  <select
                    value={item.kind}
                    onChange={(e) =>
                      updateItem(i, {
                        kind: e.target.value,
                        target_count: e.target.value === "reps" ? 10 : null,
                        target_seconds: e.target.value === "time" ? 60 : null,
                      })
                    }
                    className="bg-[#111] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white"
                  >
                    <option value="reps">Reps</option>
                    <option value="time">Time</option>
                  </select>
                  <input
                    value={item.label}
                    onChange={(e) => updateItem(i, { label: e.target.value })}
                    placeholder="What to do"
                    className="flex-1 bg-[#111] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder:text-neutral-600"
                  />
                  {item.kind === "reps" ? (
                    <input
                      type="number"
                      value={item.target_count || 0}
                      onChange={(e) => updateItem(i, { target_count: Number(e.target.value) })}
                      className="w-16 bg-[#111] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white"
                    />
                  ) : (
                    <input
                      type="number"
                      value={Math.round((item.target_seconds || 0) / 60)}
                      onChange={(e) =>
                        updateItem(i, { target_seconds: Number(e.target.value) * 60 })
                      }
                      className="w-16 bg-[#111] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white"
                    />
                  )}
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                    {item.kind === "reps" ? "reps" : "min"}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    className="text-neutral-500 hover:text-red-400 p-1"
                    aria-label="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            data-testid="propose-submit"
            disabled={saving}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-4 rounded-md transition-all disabled:opacity-60 hover:scale-[1.02]"
          >
            {saving ? "Sending…" : "Propose Set Test"} <ArrowRight size={14} />
          </button>
        </form>
      </div>

      <style>{`
        .onboard-input {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.5rem;
          padding: 0.7rem 0.9rem;
          color: #fff;
          font-size: 0.875rem;
          outline: none;
        }
        .onboard-input:focus { border-color: #C6FF00; }
      `}</style>
    </div>
  );
};

export default ProposeSetTestModal;
